import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * ModalShell — reusable centered modal used for every entry form and confirmation.
 * Always centered in the viewport (position: fixed), no matter how far the page is scrolled.
 * Uses backdrop click + Esc to close.
 */
export default function ModalShell({ title, subtitle, onClose, children, maxWidth = 640, testId = "modal" }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      data-testid={`${testId}-backdrop`}
      style={{
        position: "fixed", inset: 0, background: "rgba(2,5,9,0.72)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1200, padding: 16, overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel modal-content"
        data-testid={testId}
        style={{
          // `dvh` respects the real visible viewport on mobile PWAs (browser Chrome, safe-area).
          // A `vh` fallback keeps older browsers behaving. margin auto centres vertically inside
          // the scrollable backdrop, so tall modals never get clipped at the top.
          width: "100%", maxWidth,
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 0,
          boxShadow: "var(--shadow-3)",
          margin: "auto",
        }}
      >
        {/* Sticky header — always visible even when scrolling long forms. */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
          padding: "18px 24px 14px",
          position: "sticky", top: 0, zIndex: 2,
          background: "var(--panel)",
          borderBottom: "1px solid var(--border)",
          borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit",
        }}>
          <div>
            {subtitle && <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>{subtitle}</div>}
            <h3 style={{ fontSize: 17, margin: 0 }}>{title}</h3>
          </div>
          <button onClick={onClose} className="btn" data-testid={`${testId}-close`} aria-label="Close" style={{ padding: "6px 8px" }}><X size={14} /></button>
        </div>
        <div style={{ padding: "18px 24px 24px" }}>
          {children}
        </div>
      </div>
    </div>
  );
}
